import React, { useEffect, useState } from 'react'
import { GenericWarning, ModalSectionSideHeader, ModalTabSections } from '../../modal-components'
import i18n from 'live/services/i18n'
import {
    Errors,
    getEmptyErrors,
    RestrictedConnectionErrors,
    RestrictedPipeErrors,
    RestrictedRateOfPenetrationErrors
} from '../types'
import {
    RawActivityEvent,
    RestrictedConnection,
    RestrictedPipeMovement,
    RestrictedRateOfPenetration,
    SelectOptions
} from '../../../utils/type'
import { ConnectionTab } from './ConnectionTab'
import { PipeTab } from './PipeTab'
import { ROPTab } from './ROPTab'
import { STATIC_DATA_STATES } from '../../chart-components'

const errorMessage = i18n('Please correct the highlighted fields')

interface RestrictedPerformanceModalProps {
    restrictedPipes: RestrictedPipeMovement[]
    updateRestrictedPipes: (restrictedPipes: RestrictedPipeMovement[]) => void
    validateRestrictedPipe: (restrictedPipe: RestrictedPipeMovement) => { pass: boolean; errors: RestrictedPipeErrors }
    restrictedConnections: RestrictedConnection[]
    updateRestrictedConnections: (restrictedConnections: RestrictedConnection[]) => void
    validateRestrictedConnection: (
        restrictedConnection: RestrictedConnection
    ) => { pass: boolean; errors: RestrictedConnectionErrors }
    restrictedRateOfPenetrations: RestrictedRateOfPenetration[]
    updateRestrictedRateOfPenetrations: (restrictedRateOfPenetrations: RestrictedRateOfPenetration[]) => void
    validateRestrictedRop: (
        restrictedPipe: RestrictedRateOfPenetration
    ) => { pass: boolean; errors: RestrictedRateOfPenetrationErrors }
    tubularsIntervalPerTime: boolean
    tubularType: SelectOptions | null
    assetFilterId?: string
    showForm?: boolean
    errors: Errors
    setErrors: (newErrors: Errors) => void
    event: RawActivityEvent
    setIsAddRestrictedPipesButtonDisabled: (isDisabled: boolean) => void
    setIsAddRestrictedConnectionButtonDisabled: (isDisabled: boolean) => void
    setIsAddRestrictedROPButtonDisabled: (isDisabled: boolean) => void
}

const RestrictedPerformanceModal = ({
    restrictedPipes,
    updateRestrictedPipes,
    validateRestrictedPipe,
    restrictedConnections,
    updateRestrictedConnections,
    validateRestrictedConnection,
    restrictedRateOfPenetrations,
    updateRestrictedRateOfPenetrations,
    validateRestrictedRop,
    tubularsIntervalPerTime,
    tubularType,
    assetFilterId,
    showForm,
    errors,
    setErrors,
    event,
    setIsAddRestrictedPipesButtonDisabled,
    setIsAddRestrictedConnectionButtonDisabled,
    setIsAddRestrictedROPButtonDisabled
}: RestrictedPerformanceModalProps): JSX.Element => {
    const [selectedTab, setSelectedTab] = useState<string>('pipe')

    // RestrictedPipes
    const addRestrictedPipe = (newRestrictedPipe: RestrictedPipeMovement) => {
        updateRestrictedPipes([...(restrictedPipes ?? []), newRestrictedPipe])
    }
    const updateCurrentRestrictedPipes = (restrictedIndex: number, key: string, newValue: any) => {
        const isTheSameIndex = restrictedPipes?.some((r, i) => i === restrictedIndex)

        if (isTheSameIndex) {
            const newRestrictedPipes = [...restrictedPipes]

            newRestrictedPipes[restrictedIndex][key] = newValue
            updateRestrictedPipes(newRestrictedPipes)
        }
    }
    const deleteRestrictedPipe = (index: number) => {
        const newList = restrictedPipes.filter((_c, idx) => idx !== index)
        updateRestrictedPipes(newList)
    }

    // Restricted Connections
    const addRestrictedConnection = (newRestrictedConnection: RestrictedConnection) => {
        updateRestrictedConnections([...(restrictedConnections ?? []), newRestrictedConnection])
    }
    const updateCurrentRestrictedConnection = (restrictedIndex: number, key: string, newValue: any) => {
        const isTheSameIndex = restrictedConnections?.some((r, i) => i === restrictedIndex)

        if (isTheSameIndex) {
            const newRestrictedConnections = [...restrictedConnections]

            newRestrictedConnections[restrictedIndex][key] = newValue
            updateRestrictedConnections(newRestrictedConnections)
        }
    }
    const deleteRestrictedConnection = (index: number) => {
        const newList = restrictedConnections.filter((_c, idx) => idx !== index)
        updateRestrictedConnections(newList)
    }

    // ROP
    const addRestrictedRateOfPenetration = (newRestrictedRateOfPenetration: RestrictedRateOfPenetration) => {
        updateRestrictedRateOfPenetrations([...(restrictedRateOfPenetrations ?? []), newRestrictedRateOfPenetration])
    }
    const updateCurrentRestrictedRateOfPenetration = (restrictedIndex: number, key: string, newValue: any) => {
        const isTheSameIndex = restrictedRateOfPenetrations?.some((r, i) => i === restrictedIndex)

        if (isTheSameIndex) {
            const newRestrictedRateOfPenetrations = [...restrictedRateOfPenetrations]

            newRestrictedRateOfPenetrations[restrictedIndex][key] = newValue
            updateRestrictedRateOfPenetrations(newRestrictedRateOfPenetrations)
        }
    }
    const deleteRestrictedRateOfPenetration = (index: number) => {
        const newList = restrictedRateOfPenetrations.filter((_c, idx) => idx !== index)
        updateRestrictedRateOfPenetrations(newList)
    }

    const validateRestrictedPerformanceArrays = (
        array: RestrictedPipeMovement[] | RestrictedConnection[] | RestrictedRateOfPenetration[],
        type: string
    ): {
        pass: boolean
        errors: RestrictedPipeErrors | RestrictedConnectionErrors | RestrictedRateOfPenetrationErrors
    } => {
        const restrictedResults = array.map(restricted => {
            if (type === 'pipe') return validateRestrictedPipe(restricted as RestrictedPipeMovement)
            if (type === 'connection') return validateRestrictedConnection(restricted as RestrictedConnection)
            if (type === 'rop') return validateRestrictedRop(restricted as RestrictedRateOfPenetration)
        })
        const failedRestricted = restrictedResults.find(res => !res?.pass)

        return {
            pass: failedRestricted ? failedRestricted.pass : restrictedResults[0]?.pass,
            errors: failedRestricted ? failedRestricted.errors : restrictedResults[0]?.errors
        }
    }

    const validateFields = (): {
        pass: boolean
        errors: Errors
    } => {
        const result = { pass: true, errors: { ...errors, messages: { ...errors.messages } } }

        switch (selectedTab) {
            case 'pipe':
                const validatePipe = {
                    ...validateRestrictedPerformanceArrays(event.restrictedPipeMovements, selectedTab)
                }
                result.pass = validatePipe.pass
                result.errors.restrictedPipe = validatePipe.errors as RestrictedPipeErrors
                result.errors.messages.restrictedPerformance = validatePipe.pass ? '' : errorMessage
                break
            case 'connection':
                const validateConnection = {
                    ...validateRestrictedPerformanceArrays(event.restrictedConnections, selectedTab)
                }
                result.pass = validateConnection.pass
                result.errors.restrictedConnection = validateConnection.errors as RestrictedConnectionErrors
                result.errors.messages.restrictedPerformance = validateConnection.pass ? '' : errorMessage
                break
            case 'rop':
                const validateRop = { ...validateRestrictedPerformanceArrays(event.restrictedRops, selectedTab) }
                result.pass = validateRop.pass
                result.errors.restrictedRateOfPenetration = validateRop.errors as RestrictedRateOfPenetrationErrors
                result.errors.messages.restrictedPerformance = validateRop.pass ? '' : errorMessage
                break
            default:
                return result
        }
        setErrors({ ...(result.errors ?? errors) })
        return result
    }

    const getRestrictedArrayBySelectedTab = ():
        | RestrictedPipeMovement[]
        | RestrictedConnection[]
        | RestrictedRateOfPenetration[]
        | [] => {
        if (selectedTab === 'pipe') return event.restrictedPipeMovements
        if (selectedTab === 'connection') return event.restrictedConnections
        if (selectedTab === 'rop') return event.restrictedRops

        return []
    }

    const onChangeRestrictedTab = (tab: string) => {
        const currentRestricted = getRestrictedArrayBySelectedTab()
        if (currentRestricted.length > 0) {
            const { pass } = validateFields(event)

            if (pass) {
                setSelectedTab(tab)
            }
        } else {
            setSelectedTab(tab)
        }
    }

    useEffect(() => {
        const currentRestricted = getRestrictedArrayBySelectedTab()

        if (currentRestricted?.length === 0) {
            setErrors({ ...getEmptyErrors() })
        }
    }, [event])

    return (
        <>
            <ModalSectionSideHeader label="Restricted Performance" />
            <header className="tabs-header">
                <div
                    className={`tab ${selectedTab === 'pipe' ? 'active' : 'inactive'}`}
                    onClick={() => {
                        onChangeRestrictedTab('pipe')
                    }}
                >
                    <div>{i18n('Pipe Movement')}</div>
                </div>
                <div
                    className={`tab ${selectedTab === 'connection' ? 'active' : 'inactive'}`}
                    onClick={() => {
                        onChangeRestrictedTab('connection')
                    }}
                >
                    <div>{i18n('Connection')}</div>
                </div>
                <div
                    className={`tab ${selectedTab === 'rop' ? 'active' : 'inactive'}`}
                    onClick={() => {
                        onChangeRestrictedTab('rop')
                    }}
                >
                    <div>{i18n('ROP')}</div>
                </div>
            </header>
            <ModalTabSections
                selected={selectedTab === 'pipe'}
                content={
                    <div onMouseDown={e => e.stopPropagation()}>
                        <PipeTab
                            restrictedPipes={restrictedPipes}
                            errors={errors.restrictedPipe}
                            validateRestrictedPipeFields={validateFields}
                            addRestrictedPipe={addRestrictedPipe}
                            updateCurrentRestrictedPipes={updateCurrentRestrictedPipes}
                            deleteRestrictedPipe={deleteRestrictedPipe}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            tubularType={tubularType}
                            assetFilterId={assetFilterId}
                            setIsAddRestrictedPipesButtonDisabled={setIsAddRestrictedPipesButtonDisabled}
                            tubularEvent={event}
                        />
                    </div>
                }
            />
            <ModalTabSections
                selected={selectedTab === 'connection'}
                content={
                    <div onMouseDown={e => e.stopPropagation()}>
                        <ConnectionTab
                            restrictedConnections={restrictedConnections}
                            errors={errors.restrictedConnection}
                            validateRestrictedConnectionFields={validateFields}
                            addRestrictedConnection={addRestrictedConnection}
                            updateCurrentRestrictedConnection={updateCurrentRestrictedConnection}
                            deleteRestrictedConnection={deleteRestrictedConnection}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            tubularType={tubularType}
                            assetFilterId={assetFilterId}
                            setIsAddRestrictedConnectionButtonDisabled={setIsAddRestrictedConnectionButtonDisabled}
                            tubularEvent={event}
                        />
                    </div>
                }
            />
            <ModalTabSections
                selected={selectedTab === 'rop'}
                content={
                    <div onMouseDown={e => e.stopPropagation()}>
                        <ROPTab
                            restrictedRateOfPenetrations={restrictedRateOfPenetrations}
                            errors={errors.restrictedRateOfPenetration}
                            validateRestrictedRateOfPenetrationFields={validateFields}
                            addRestrictedRateOfPenetration={addRestrictedRateOfPenetration}
                            updateCurrentRestrictedRateOfPenetration={updateCurrentRestrictedRateOfPenetration}
                            deleteRestrictedRateOfPenetration={deleteRestrictedRateOfPenetration}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            tubularType={tubularType}
                            showForm={showForm}
                            assetFilterId={assetFilterId}
                            setIsAddRestrictedROPButtonDisabled={setIsAddRestrictedROPButtonDisabled}
                            tubularEvent={event}
                        />
                    </div>
                }
            />
            <GenericWarning message={errors?.messages?.restrictedPerformance} />
        </>
    )
}

export default RestrictedPerformanceModal
